import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Sparkles, ArrowRight, MapPin, Star, Check, ChevronRight, MessageCircle,
  Utensils, Hotel, Waves, Compass, CalendarDays, Tag, HeartPulse, Building2, Briefcase, Store,
  Zap, TrendingUp, Users, ShieldCheck, Car,
} from "lucide-react";
import SEO from "../../components/SEO";
import SafeImage from "../../components/SafeImage";
import BannerAd from "../../components/BannerAd";
import BoostedRail from "../../components/BoostedRail";
import OnlineUsersBadge from "../../components/OnlineUsersBadge";
import HomeNewsBlocks from "../../components/site/HomeNewsBlocks";
import { api } from "../../lib/api";
import { CATEGORIES, SEARCH_CATEGORIES, SITE_PLANS, VERTICAL_PLANS, IMAGES, AI_EXAMPLES, buildSearchUrl, track } from "../../lib/institutional";

const ICONS = { Utensils, Hotel, Waves, Compass, CalendarDays, Tag, HeartPulse, Building2, Briefcase, Store, Car };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: Math.min(i * 0.06, 0.4) } }),
};

function Section({ id, className = "", children }) {
  return <section id={id} className={`max-w-7xl mx-auto px-4 md:px-8 ${className}`}>{children}</section>;
}

// Banners spread across the page (wide formats), rotating so neighbours differ.
function InlineBanner({ banners, index }) {
  const wide = banners.filter((b) => b.iab_format !== "medium_rectangle");
  if (!wide.length) return null;
  const b = wide[(index + 1) % wide.length];
  return <Section className="py-6"><BannerAd banner={b} /></Section>;
}

export default function Landing() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [bairro, setBairro] = useState("");
  const [featured, setFeatured] = useState([]);
  const [banners, setBanners] = useState([]);
  const plansRef = useRef(null);

  useEffect(() => {
    api.get("/businesses?limit=6").then(({ data }) => setFeatured((data.items || data || []).slice(0, 6))).catch(() => {});
    api.get("/banners", { params: { placement: "landing" } }).then(({ data }) => setBanners(data.items || [])).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    track("institutional_search", { q, category, bairro });
    navigate(buildSearchUrl({ q, category, bairro }));
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Organization", name: "Guia de Jampa AI", url: "https://guiadejampa.com/", logo: "https://guiadejampa.com/logo-h.png" },
      { "@type": "WebSite", name: "Guia de Jampa AI", url: "https://guiadejampa.com/", inLanguage: "pt-BR",
        potentialAction: { "@type": "SearchAction", target: "https://guiadejampa.com/empresas?q={search_term_string}", "query-input": "required name=search_term_string" } },
      { "@type": "SoftwareApplication", name: "Guia de Jampa AI", applicationCategory: "TravelApplication", operatingSystem: "Web, Android, iOS", offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" } },
    ],
  };

  return (
    <div data-testid="institutional-home" className="bg-white">
      <SEO
        title="Empresas, Turismo, Eventos e Restaurantes em João Pessoa"
        description="Descubra empresas, restaurantes, hotéis, praias, eventos, promoções, turismo e serviços em João Pessoa com o Guia de Jampa AI, a plataforma inteligente da cidade."
        path="/"
        image={IMAGES.hero}
        keywords="Guia de Jampa, Guia de Jampa AI, João Pessoa PB, empresas em João Pessoa, restaurantes em João Pessoa, hotéis em João Pessoa, praias de João Pessoa, turismo em João Pessoa, eventos em João Pessoa, o que fazer em João Pessoa"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img
          src={IMAGES.heroLg}
          srcSet={`${IMAGES.heroSm} 640w, ${IMAGES.heroMd} 960w, ${IMAGES.heroLg} 1280w, ${IMAGES.heroXl} 1600w`}
          sizes="100vw"
          width="1280"
          height="720"
          fetchpriority="high"
          decoding="async"
          alt="Vista aérea da orla de João Pessoa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/55 to-slate-900/80" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16 w-full text-white">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex flex-wrap items-center gap-3 mb-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md px-4 py-1.5 text-xs font-semibold tracking-wide">
              <Sparkles className="w-4 h-4" /> A INTELIGÊNCIA ARTIFICIAL OFICIAL DE JOÃO PESSOA
            </span>
            <OnlineUsersBadge />
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            Descubra João Pessoa com <span className="text-gradient">Inteligência Artificial</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-5 text-base md:text-lg text-white/90 max-w-2xl">
            Empresas, restaurantes, praias, eventos, promoções, turismo e tudo o que acontece na cidade, reunidos em uma única plataforma inteligente.
          </motion.p>
          <motion.p variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-2 text-sm text-white/80 max-w-2xl">
            O Guia de Jampa AI conecta moradores, turistas e empresas às melhores experiências de João Pessoa, com dados reais, localização e inteligência artificial.
          </motion.p>

          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-7 flex flex-wrap gap-3">
            <button data-testid="hero-explore" onClick={() => { track("hero_cta", { cta: "explorar" }); navigate("/feed"); }} className="px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold premium-shadow press flex items-center gap-2">
              Explorar João Pessoa <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-ask-ai" onClick={() => { track("hero_cta", { cta: "ia" }); navigate("/ia"); }} className="px-5 py-3 rounded-full bg-white text-slate-900 font-semibold press flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Perguntar para a IA
            </button>
            <button data-testid="hero-register-business" onClick={() => { track("hero_cta", { cta: "empresa" }); navigate("/minha-empresa"); }} className="px-5 py-3 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors">
              Cadastrar minha empresa
            </button>
          </motion.div>

          {/* Search */}
          <motion.div variants={fadeUp} custom={5} initial="hidden" animate="show" className="mt-9 max-w-4xl">
            <p className="font-heading font-semibold text-lg mb-3">O que você deseja encontrar em João Pessoa?</p>
            <form onSubmit={doSearch} data-testid="institutional-search" className="bg-white rounded-2xl md:rounded-full p-2 flex flex-col md:flex-row gap-2 shadow-2xl">
              <div className="flex items-center flex-1 px-3">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input data-testid="search-term" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Restaurante, hotel, evento, praia, empresa ou serviço..." className="w-full px-3 py-3 text-slate-800 outline-none bg-transparent" />
              </div>
              <select
                data-testid="search-category"
                id="search-category"
                aria-label="Categoria de busca"
                value={category}
                onChange={(e) => { setCategory(e.target.value); track("category_select", { category: e.target.value }); }}
                className="px-4 py-3 text-slate-700 outline-none bg-slate-50 md:bg-transparent rounded-xl md:rounded-none md:border-l border-slate-200">
                <option value="">Categoria</option>
                {SEARCH_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <label htmlFor="search-category" className="sr-only">Categoria de busca</label>
              <input data-testid="search-bairro" value={bairro} onChange={(e) => setBairro(e.target.value)} placeholder="Bairro" className="px-4 py-3 text-slate-700 outline-none bg-slate-50 md:bg-transparent rounded-xl md:rounded-none md:border-l border-slate-200 md:w-36" />
              <button data-testid="search-submit" type="submit" className="px-6 py-3 rounded-xl md:rounded-full bg-brand-gradient text-white font-semibold press flex items-center justify-center gap-2">
                <Search className="w-4 h-4" /> Buscar
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* NEWS BLOCKS — right after the hero, banners distributed organically */}
      <HomeNewsBlocks banners={banners} />

      {/* IMPULSIONADOS — rotating boosted listings */}
      <Section className="pt-6 md:pt-10">
        <BoostedRail limit={8} />
      </Section>

      {/* CATEGORIES */}
      <Section id="categorias" className="py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Explore</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Explore João Pessoa</h2>
          <p className="text-slate-500 mt-3">Tudo o que a cidade oferece, a um clique de distância.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.icon] || Store;
            return (
              <motion.button key={c.key} data-testid={`cat-${c.key}`} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                onClick={() => { track("category_click", { category: c.key }); navigate(c.href); }}
                className="group relative rounded-2xl overflow-hidden text-left soft-shadow bg-white hover:-translate-y-1 transition-transform">
                <div className="relative h-32">
                  <SafeImage src={c.image} category={c.key} alt={c.label} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-heading font-bold text-slate-900">{c.label}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{c.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-2">Explorar <ChevronRight className="w-3.5 h-3.5" /></span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </Section>

      <InlineBanner banners={banners} index={0} />

      {/* AI SECTION */}
      <div className="bg-slate-50 py-16 md:py-24">
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Inteligência Artificial</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Pergunte qualquer coisa sobre João Pessoa</h2>
              <p className="text-slate-500 mt-4 leading-relaxed">
                A IA do Guia de Jampa responde utilizando informações reais da plataforma, empresas cadastradas, eventos, notícias, praias, turismo e dados locais.
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {AI_EXAMPLES.map((ex) => (
                  <button key={ex} onClick={() => { track("ai_example", { q: ex }); navigate(`/ia?q=${encodeURIComponent(ex)}`); }} className="text-sm px-3 py-2 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary transition-colors">
                    {ex}
                  </button>
                ))}
              </div>
              <button data-testid="ai-cta" onClick={() => { track("open_ai", { from: "section" }); navigate("/ia"); }} className="mt-7 px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold premium-shadow press inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Conversar com a IA
              </button>
            </div>
            <div className="rounded-3xl bg-white soft-shadow p-5">
              <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
                <div className="w-9 h-9 rounded-xl bg-brand-gradient flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
                <div><p className="font-heading font-bold text-slate-900 text-sm">Guia de Jampa AI</p><p className="text-xs text-slate-400">Online agora</p></div>
              </div>
              <div className="py-4 space-y-3">
                <div className="max-w-[85%] ml-auto bg-primary text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">Onde jantar hoje em Tambaú?</div>
                <div className="max-w-[90%] bg-slate-100 text-slate-700 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm">
                  Em Tambaú você encontra ótimas opções de frutos do mar à beira-mar. Posso sugerir restaurantes em destaque com base nas empresas cadastradas e na sua localização. 🌊
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* FEATURED BUSINESSES */}
      {featured.length > 0 && (
        <Section className="py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Destaques</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Empresas em destaque em João Pessoa</h2>
            </div>
            <button onClick={() => navigate("/empresas")} className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-primary">Ver todas <ArrowRight className="w-4 h-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((b, i) => (
              <motion.button key={b.id || b.slug} data-testid={`featured-${b.slug}`} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                onClick={() => { track("featured_click", { slug: b.slug }); navigate(`/empresa/${b.slug}`); }}
                className="text-left rounded-2xl overflow-hidden bg-white soft-shadow hover:-translate-y-1 transition-transform">
                <div className="relative h-44">
                  <SafeImage src={b.cover} category={b.category} alt={b.name} loading="lazy" className="w-full h-full object-cover" />
                  {b.plan && b.plan !== "free" && (
                    <span className="absolute top-3 left-3 rounded-full bg-brand-gradient text-white text-[11px] font-bold px-3 py-1 capitalize">{b.plan}</span>
                  )}
                </div>
                <div className="p-5">
                  <p className="font-heading font-bold text-slate-900">{b.name}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {b.neighborhood || b.address || "João Pessoa"}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-3">Ver empresa <ChevronRight className="w-4 h-4" /></span>
                </div>
              </motion.button>
            ))}
          </div>
        </Section>
      )}

      <InlineBanner banners={banners} index={1} />

      {/* HOW IT WORKS */}
      <div className="bg-slate-50 py-16 md:py-24">
        <Section>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Como Funciona</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">João Pessoa em suas mãos</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Search, t: "Pesquise", d: "Encontre empresas, lugares, eventos e serviços." },
              { icon: Sparkles, t: "Pergunte à IA", d: "Receba sugestões personalizadas com dados reais." },
              { icon: Compass, t: "Descubra", d: "Explore mapas, promoções, praias, turismo e acontecimentos." },
            ].map((s, i) => (
              <motion.div key={s.t} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="bg-white rounded-2xl p-7 soft-shadow">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><s.icon className="w-6 h-6 text-primary" /></div>
                <p className="font-heading font-bold text-lg text-slate-900">{i + 1}. {s.t}</p>
                <p className="text-slate-500 mt-2 text-sm">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* FOR BUSINESS */}
      <Section className="py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden h-80 lg:h-96">
            <SafeImage src={IMAGES.storefront} category="empresas" alt="Empresa local em João Pessoa" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Para Empresas</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Faça sua empresa ser encontrada em João Pessoa</h2>
            <p className="text-slate-500 mt-4">Crie uma página empresarial completa, apareça nas buscas, no mapa e nas recomendações inteligentes do Guia de Jampa AI.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {["Página empresarial", "Presença no mapa", "Botão de WhatsApp", "Produtos e serviços", "Promoções e cupons", "Métricas e leads", "SEO local", "Destaque conforme o plano"].map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-success shrink-0" /> {b}</div>
              ))}
            </div>
            <button data-testid="for-business-cta" onClick={() => { track("register_business", { from: "section" }); navigate("/minha-empresa"); }} className="mt-7 px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold premium-shadow press inline-flex items-center gap-2">
              Cadastrar minha empresa <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Section>

      {/* PLANS — 5 core plans */}
      <div ref={plansRef} className="bg-slate-50 py-16 md:py-24 scroll-mt-24">
        <Section id="planos">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Planos</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Escolha o plano ideal</h2>
            <p className="text-slate-500 mt-3">Do morador ao negócio premium. Pague com Pix ou cartão via Mercado Pago.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 items-stretch">
            {SITE_PLANS.map((p) => (
              <div key={p.key} data-testid={`site-plan-${p.key}`} className={`relative rounded-2xl bg-white p-6 flex flex-col ${p.highlight ? "ring-2 ring-primary premium-shadow lg:-mt-3" : "soft-shadow"}`}>
                {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-gradient text-white text-[11px] font-bold px-3 py-1 whitespace-nowrap">{p.badge}</span>}
                <p className="font-heading font-bold text-lg text-slate-900">{p.name}</p>
                <p className="mt-2 font-heading font-extrabold text-2xl text-slate-900">{p.price}<span className="text-sm font-medium text-slate-400">{p.period}</span></p>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-success shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                <button data-testid={`site-plan-cta-${p.key}`} onClick={() => { track("plan_click", { plan: p.key }); navigate(p.href); }}
                  className={`mt-6 w-full py-2.5 rounded-full font-semibold press ${p.highlight ? "bg-brand-gradient text-white" : "border border-slate-200 text-slate-700 hover:border-primary hover:text-primary transition-colors"}`}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>

          {/* VERTICAL PLANS — Imóveis, Médicos, Auto */}
          <div className="mt-20 md:mt-24">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Planos por vertical</p>
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Anuncie no seu segmento</h3>
              <p className="text-slate-500 mt-3">Verticais dedicadas com até 50 anúncios/mês, dados estruturados e destaque no feed correto.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {VERTICAL_PLANS.map((p, i) => {
                const Icon = ICONS[p.icon] || Store;
                return (
                  <motion.div
                    key={p.key}
                    data-testid={`vertical-plan-${p.key}`}
                    variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                    className="relative rounded-3xl overflow-hidden bg-white p-6 flex flex-col soft-shadow hover:-translate-y-1 transition-transform"
                  >
                    <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${p.accent}`} />
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.accent} flex items-center justify-center text-white shadow-lg`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{p.tag}</span>
                    </div>
                    <p className="font-heading font-bold text-xl text-slate-900 mt-4">{p.name}</p>
                    <p className="mt-1 font-heading font-extrabold text-3xl text-slate-900">
                      {p.price}<span className="text-sm font-medium text-slate-400">{p.period}</span>
                    </p>
                    <ul className="mt-5 space-y-2 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="w-4 h-4 text-success shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`vertical-plan-cta-${p.key}`}
                      onClick={() => { track("plan_click", { plan: p.key }); navigate(p.href); }}
                      className={`mt-6 w-full py-2.5 rounded-full font-semibold press text-white bg-gradient-to-r ${p.accent} shadow-md hover:opacity-95`}
                    >
                      {p.cta}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Section>
      </div>

      <InlineBanner banners={banners} index={2} />

      {/* CTA BAND */}
      <section className="relative py-20">
        <img src={IMAGES.sunset} alt="Pôr do sol em João Pessoa" width="1280" height="720" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/65" />
        <div className="relative max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl">Viva João Pessoa como nunca antes</h2>
          <p className="mt-3 text-white/85">Moradores, turistas e empresas em uma única plataforma inteligente.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={() => { track("open_app", { from: "cta_band" }); navigate("/feed"); }} className="px-6 py-3 rounded-full bg-white text-slate-900 font-semibold press">Acessar o App</button>
            <button onClick={() => { track("open_ai", { from: "cta_band" }); navigate("/ia"); }} className="px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Perguntar à IA</button>
          </div>
        </div>
      </section>
    </div>
  );
}
